/**
 * Where a printed figure lies, as a box — enough to tell whether two figures
 * on a sheet would be printed over each other (P-7, Generalprobe 15.10.2026).
 *
 * On a sheet the figures of one wall are packed against each other already
 * (`SheetLabels`' rows); what that packing cannot see is the **neighbouring
 * wall**. At a short Rücksprung (a wall under 2 m between two long ones) the
 * figures of both walls stand in the same corner — „1.000 · 750" over
 * „6×1.000 · 600". These boxes are the one check across walls.
 *
 * Estimated, not measured: a sheet is laid out before anything is in the
 * DOM, and Roboto Mono's fixed advance makes the width of a figure a
 * multiplication, not a measurement.
 */

import type { ScreenPoint } from "./projection";

/** An axis-aligned box in board units. */
export interface LabelBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * The axis-aligned box around a text of `width` × `height`, centred on
 * `centre` and turned by `angleDeg` (SVG's sense: clockwise, degrees).
 */
export function labelBox(centre: ScreenPoint, angleDeg: number, width: number, height: number): LabelBox {
  const radians = (angleDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  const halfX = (width * cos + height * sin) / 2;
  const halfY = (width * sin + height * cos) / 2;
  return {
    left: centre.x - halfX,
    top: centre.y - halfY,
    right: centre.x + halfX,
    bottom: centre.y + halfY,
  };
}

/** Whether two boxes come closer than `gap` — touching counts as clear. */
export function boxesOverlap(a: LabelBox, b: LabelBox, gap = 0): boolean {
  return (
    a.left < b.right + gap &&
    b.left < a.right + gap &&
    a.top < b.bottom + gap &&
    b.top < a.bottom + gap
  );
}

/** Whether `box` comes closer than `gap` to any box already standing. */
export function collides(box: LabelBox, standing: readonly LabelBox[], gap = 0): boolean {
  return standing.some((each) => boxesOverlap(box, each, gap));
}
